import { Helmet } from "react-helmet";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { pget } from "@/api";
import { Models } from "@/api/types";
import { RepeatMDFooter } from "@/atoms/repeatmd-footer/RepeatMDFooter";
import { GiftCardClaimContainer } from "@/modules/giftCards/GiftCardClaimContainer.tsx/GiftCardClaimContainer";
import { waitObj } from "@/toolbox/Promise";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import PageError from "atoms/icon/icons/page_error.svg";

import { queryClient, ThemeContext } from "../Root";

const getGiftCardTransferItem = pget(
  "/gift-cards/transfer/{giftCardTransferItemId}/anonymous"
);

const getPlatform = pget("/platform/current");

const getPublicListings = pget("/listings/public");

const load = async ({ id }: { id: string }) => {
  const transfer = await queryClient.fetchQuery(
    getGiftCardTransferItem.key({ giftCardTransferItemId: id }),
    () =>
      getGiftCardTransferItem({
        giftCardTransferItemId: id,
      })
  );

  const platform = await queryClient.fetchQuery(getPlatform.key(), () =>
    getPlatform()
  );

  const listings = await queryClient
    .fetchQuery(getPublicListings.key(), () => getPublicListings())
    .catch(() => []);

  // Get top 4 listings by sell volume
  const topListings = [...(listings || [])]
    .sort((a, b) => (b.sellVolume || 0) - (a.sellVolume || 0))
    .slice(0, 5);

  return waitObj({
    transfer,
    platform,
    topListings,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const claimGiftCardLoader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);
  const id = url.searchParams.get("id");
  if (!id) {
    throw redirect("/");
  }
  return load({ id });
};

export default function ClaimGiftCardPage() {
  const navigator = useNavigate();
  const { transfer, platform, topListings } = useLoaderData() as LoaderData;

  const amountInDollars = transfer.giftCardItemAmountInCents / 100;

  const isAlreadyRedeemed = !!transfer.acknowledgedAtUtc;
  const isValidForCurrentPlatform = platform.id === transfer.platformId;

  const renderRedeemedError = () => {
    const meta = getRedeemedErrorMeta();

    return (
      <>
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
        </Helmet>
        <div className="flex min-h-screen flex-col items-center justify-center bg-white px-8">
          <div className="flex flex-col items-center gap-6 text-center">
            <Icon svg={PageError} color="darkGrey" size="large" />
            <div className="space-y-2">
              <h1 className="text-h1 font-bold text-primary">{meta.title}</h1>
              <p className="text-body1 text-gray-600">{meta.description}</p>
            </div>
            <Button onClick={() => navigator("/")}>Go to Homepage</Button>
          </div>
          <div className="mt-8">
            <RepeatMDFooter showLinks />
          </div>
        </div>
      </>
    );
  };

  const renderInvalidPlatformError = () => {
    const meta = getInvalidPlatformErrorMeta();

    return (
      <>
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
        </Helmet>
        <div className="flex min-h-screen flex-col items-center justify-center bg-white px-8">
          <div className="flex flex-col items-center gap-6 text-center">
            <Icon svg={PageError} color="darkGrey" size="large" />
            <div className="space-y-2">
              <h1 className="text-h1 font-bold text-primary">{meta.title}</h1>
              <p className="text-body1 text-gray-600">{meta.description}</p>
            </div>
            <Button onClick={() => navigator("/")}>Go to Homepage</Button>
          </div>
          <div className="mt-8">
            <RepeatMDFooter showLinks />
          </div>
        </div>
      </>
    );
  };

  const renderGiftCardClaim = () => {
    const meta = getGiftCardClaimMeta(transfer, amountInDollars);

    return (
      <>
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <meta property="og:title" content={meta.ogTitle} />
          <meta property="og:description" content={meta.ogDescription} />
          <meta property="og:image" content={meta.imageUrl} />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={meta.ogTitle} />
          <meta name="twitter:description" content={meta.ogDescription} />
          <meta name="twitter:image" content={meta.imageUrl} />
        </Helmet>

        <GiftCardClaimContainer
          platform={platform}
          transfer={transfer}
          topListings={topListings}
        />
      </>
    );
  };

  if (!platform || !isValidForCurrentPlatform) {
    return renderInvalidPlatformError();
  }

  if (isAlreadyRedeemed) {
    return renderRedeemedError();
  }

  return renderGiftCardClaim();
}

// Meta data generation functions
const getRedeemedErrorMeta = () => ({
  title: "Gift Card Already Redeemed",
  description:
    "This gift card has already been redeemed and cannot be claimed again.",
});

const getInvalidPlatformErrorMeta = () => ({
  title: "Invalid Gift Card",
  description: "This gift card is invalid for the current location.",
});

const getGiftCardClaimMeta = (
  transfer: Models<"DisplayGiftCardTransferAnonymous">,
  amountInDollars: number
) => {
  const title = `${transfer.senderFirstName} sent you a $${amountInDollars} gift card to ${transfer.platformName}`;
  const description =
    transfer.personalMessage ||
    `${transfer.senderFirstName} sent you a gift card to ${transfer.locationName}`;
  const ogTitle = `${transfer.senderFirstName} sent you a $${amountInDollars} gift card`;
  const ogDescription =
    transfer.personalMessage ||
    `Claim your gift card to ${transfer.locationName} and start your wellness journey!`;
  const imageUrl =
    transfer.giftCardListingMediaItemUri || transfer.platformLogo || "";

  return {
    title,
    description,
    ogTitle,
    ogDescription,
    imageUrl,
  };
};
